import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { LineChart, Line, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from "recharts";
import {
  ChevronLeft,
  Palette,
  Lightbulb,
  CheckCircle2,
  Brain,
  Sparkles,
  Copy,
  ExternalLink,
  Star,
  Eye,
  Zap,
  Target,
  TrendingUp,
  AlertTriangle,
  Info,
  Plus,
  Pencil,
  Trash2,
  Save
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const cardHoverVariants = {
  rest: { y: 0, boxShadow: "var(--shadow-sm)" },
  hover: { y: -2, boxShadow: "var(--shadow-md)" }
};

// Psychology bias definitions for hover cards
const biasDefinitions = {
  "curiosity-gap": {
    name: "Curiosity Gap",
    description: "Erzeugt ein Informationsdefizit, das der Viewer schließen will.",
    example: "\"Du wirst nicht glauben, was passiert ist...\"",
    doTip: "Klare Andeutung ohne vollständige Auflösung",
    dontTip: "Clickbait ohne echten Inhalt"
  },
  "loss-aversion": {
    name: "Verlustaversion",
    description: "Menschen fürchten Verluste mehr als sie Gewinne schätzen.",
    example: "\"Diese Fehler kosten dich tausende Euro\"",
    doTip: "Echte Risiken aufzeigen",
    dontTip: "Übertriebene Angstmache"
  },
  "social-proof": {
    name: "Social Proof",
    description: "Menschen orientieren sich am Verhalten anderer.",
    example: "\"1 Million Menschen haben das bereits gemacht\"",
    doTip: "Echte Zahlen und Testimonials",
    dontTip: "Gefälschte Statistiken"
  },
  "scarcity": {
    name: "Knappheit",
    description: "Seltene Dinge werden als wertvoller wahrgenommen.",
    example: "\"Nur noch heute verfügbar\"",
    doTip: "Echte Zeitlimits oder limitierte Angebote",
    dontTip: "Künstliche Verknappung"
  },
  "fomo": {
    name: "FOMO",
    description: "Fear of Missing Out - Angst etwas zu verpassen.",
    example: "\"Alle sprechen darüber\"",
    doTip: "Aktuelle Trends aufgreifen",
    dontTip: "Irrelevante Trends erzwingen"
  }
};

const triggerDefinitions = {
  "big-numbers": {
    name: "Große Zahlen",
    description: "Zahlen wie $1,000,000 oder 10X aktivieren das Belohnungszentrum.",
    tip: "Je größer und konkreter, desto besser"
  },
  "transformation": {
    name: "Transformation",
    description: "Vorher/Nachher-Vergleiche zeigen mögliche Veränderung.",
    tip: "Klarer visueller Kontrast ist wichtig"
  },
  "surprise-reveal": {
    name: "Überraschungs-Enthüllung",
    description: "Unerwartete Wendungen oder Geheimnisse.",
    tip: "Das Überraschungselement andeuten, nicht verraten"
  },
  "recognizable faces": {
    name: "Bekannte Gesichter",
    description: "Prominente oder bekannte Personen ziehen Aufmerksamkeit.",
    tip: "Authentische Reaktionen zeigen"
  }
};

// Small trend chart component
const TrendChart = ({ data, color = "hsl(var(--primary))" }) => {
  if (!data || data.length === 0) return null;
  
  return (
    <div className="h-16 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data}>
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke={color} 
            strokeWidth={2} 
            dot={false}
          />
          <RechartsTooltip 
            contentStyle={{ 
              background: "hsl(var(--card))", 
              border: "1px solid hsl(var(--border))",
              borderRadius: "var(--radius)",
              fontSize: "12px"
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

// Psychology Bias Card with HoverCard explanation
const BiasCard = ({ bias, count }) => {
  const definition = biasDefinitions[bias.toLowerCase()] || {
    name: bias,
    description: "Psychologischer Trigger für erhöhte Klickrate",
    example: "",
    doTip: "",
    dontTip: ""
  };

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          className="cursor-help"
        >
          <Badge 
            variant="secondary" 
            className="bg-purple-500/10 text-purple-700 dark:text-purple-300 hover:bg-purple-500/20 transition-colors"
          >
            <Brain className="h-3 w-3 mr-1" />
            {bias}
            {count > 1 && <span className="ml-1 opacity-60">×{count}</span>}
          </Badge>
        </motion.div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80" side="top">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">{definition.name}</h4>
          <p className="text-xs text-muted-foreground">{definition.description}</p>
          {definition.example && (
            <p className="text-xs italic text-muted-foreground">"{definition.example}"</p>
          )}
          {(definition.doTip || definition.dontTip) && (
            <div className="pt-2 border-t space-y-1">
              {definition.doTip && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  ✅ {definition.doTip}
                </p>
              )}
              {definition.dontTip && (
                <p className="text-xs text-red-500">
                  ❌ {definition.dontTip}
                </p>
              )}
            </div>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

// Dopamine Trigger Card
const TriggerCard = ({ trigger, count }) => {
  const definition = triggerDefinitions[trigger.toLowerCase()] || {
    name: trigger,
    description: "Dopamin-aktivierender visueller Trigger",
    tip: ""
  };

  return (
    <HoverCard openDelay={100}>
      <HoverCardTrigger asChild>
        <motion.div
          variants={itemVariants}
          whileHover={{ scale: 1.02 }}
          className="cursor-help"
        >
          <Badge 
            variant="secondary" 
            className="bg-orange-500/10 text-orange-700 dark:text-orange-300 hover:bg-orange-500/20 transition-colors"
          >
            <Zap className="h-3 w-3 mr-1" />
            {trigger}
            {count > 1 && <span className="ml-1 opacity-60">×{count}</span>}
          </Badge>
        </motion.div>
      </HoverCardTrigger>
      <HoverCardContent className="w-72" side="top">
        <div className="space-y-2">
          <h4 className="font-semibold text-sm">{definition.name}</h4>
          <p className="text-xs text-muted-foreground">{definition.description}</p>
          {definition.tip && (
            <p className="text-xs text-primary mt-2">
              💡 {definition.tip}
            </p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export const StyleDetailPage = () => {
  const { styleId } = useParams();
  const navigate = useNavigate();
  const [style, setStyle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("visual");

  useEffect(() => {
    fetchStyleDetails();
  }, [styleId]);

  const fetchStyleDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/styles/${styleId}`);
      setStyle(response.data);
    } catch (err) {
      console.error("Error fetching style:", err);
      // Only show error and navigate if we don't have existing data
      // This prevents disappearing data during server busy times
      if (!style) {
        toast.error("Style konnte nicht geladen werden");
        navigate("/styles");
      } else {
        console.log("Keeping existing style data during server busy time");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyColor = (hex) => {
    navigator.clipboard.writeText(hex);
    toast.success(`${hex} kopiert!`);
  };

  const handleUseInGenerator = () => {
    navigate(`/generator?style=${styleId}`);
  };

  // Aggregate psychology data from individual analyses
  const getPsychologyData = () => {
    if (!style?.individual_analyses?.length) return null;

    const biasCount = {};
    const triggerCount = {};
    const alwaysPresent = new Set();
    const neverPresent = new Set();
    const scrollStopPowers = [];
    const viralFormulas = [];

    style.individual_analyses.forEach(ia => {
      const sa = ia.style_analysis || {};
      const psych = sa.psychology || {};
      const viral = sa.viral_formula || {};

      if (psych.cognitive_biases) {
        psych.cognitive_biases.forEach(b => {
          biasCount[b] = (biasCount[b] || 0) + 1;
        });
      }
      if (psych.dopamine_triggers) {
        psych.dopamine_triggers.forEach(t => {
          triggerCount[t] = (triggerCount[t] || 0) + 1;
        });
      }
      if (psych.scroll_stop_power) {
        scrollStopPowers.push(psych.scroll_stop_power);
      }
      if (viral.always_present) {
        viral.always_present.forEach(e => alwaysPresent.add(e));
      }
      if (viral.never_present) {
        viral.never_present.forEach(e => neverPresent.add(e));
      }
      if (viral.formula_pattern) {
        viralFormulas.push(viral.formula_pattern);
      }
    });

    const highPowerCount = scrollStopPowers.filter(p => p === 'high' || p === 'extreme').length;

    return {
      biases: Object.entries(biasCount).sort((a, b) => b[1] - a[1]),
      triggers: Object.entries(triggerCount).sort((a, b) => b[1] - a[1]),
      alwaysPresent: Array.from(alwaysPresent),
      neverPresent: Array.from(neverPresent),
      scrollStopPower: { high: highPowerCount, total: scrollStopPowers.length },
      viralFormula: viralFormulas[0] || null
    };
  };

  // Generate mock trend data for visualization
  const generateTrendData = () => {
    return Array.from({ length: 7 }, (_, i) => ({
      day: `T${i + 1}`,
      value: Math.floor(Math.random() * 40) + 60
    }));
  };

  if (isLoading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Skeleton Topbar */}
        <div className="h-14 flex items-center gap-3 mb-6">
          <Skeleton className="h-4 w-48" />
          <div className="ml-auto flex gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-40" />
          </div>
        </div>
        
        {/* Skeleton Content */}
        <div className="grid md:grid-cols-12 gap-6">
          <div className="md:col-span-4 space-y-4">
            <Skeleton className="h-48 rounded-lg" />
            <Skeleton className="h-64 rounded-lg" />
          </div>
          <div className="md:col-span-8 space-y-4">
            <Skeleton className="h-64 rounded-lg" />
            <Skeleton className="h-48 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  if (!style) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground/50 mx-auto mb-3" />
            <h3 className="font-medium mb-1">Style nicht gefunden</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Der angeforderte Style existiert nicht oder wurde gelöscht.
            </p>
            <Button onClick={() => navigate("/styles")}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Zurück zur Style Library
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const psychologyData = getPsychologyData();
  const aggregatedStyle = style.aggregated_style || {};

  return (
    <motion.div 
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Sticky Topbar */}
      <div className="sticky top-0 z-30 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
        <div className="h-14 flex items-center gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/styles" className="text-muted-foreground hover:text-foreground transition-colors" data-testid="breadcrumb-styles-link">
                    Style Library
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage data-testid="breadcrumb-style-name">{style.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="ml-auto flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="style-copy-link-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Link kopieren</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            
            <Button 
              variant="secondary" 
              size="sm"
              onClick={() => toast.info("Feature kommt bald!")}
              data-testid="style-set-default-button"
            >
              <Star className="h-4 w-4 mr-1" />
              Als Default
            </Button>
            
            <Button 
              size="sm"
              onClick={handleUseInGenerator}
              data-testid="style-use-in-generator-button"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Im Generator verwenden
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="md:grid md:grid-cols-12 gap-6 mt-6 pb-8">
        {/* Left Sidebar - Inspector */}
        <aside className="md:col-span-4 space-y-4 md:sticky md:top-20 h-max md:max-h-[calc(100vh-6rem)] md:overflow-y-auto pr-2">
          {/* Overview Card */}
          <motion.div variants={itemVariants}>
            <Card className="overflow-hidden">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{style.name}</CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#FF0000">
                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
                        <polygon fill="#FFFFFF" points="9.545,15.568 15.818,12 9.545,8.432" />
                      </svg>
                      {style.channel_name || "YouTube Kanal"}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {style.successful_analyses || 0} Thumbnails
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Color Palette Preview */}
                {aggregatedStyle.dominant_palette?.length > 0 && (
                  <div className="flex gap-1">
                    {aggregatedStyle.dominant_palette.slice(0, 5).map((color, idx) => (
                      <TooltipProvider key={idx}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => handleCopyColor(color.hex)}
                              className="flex-1 h-8 rounded-md border transition-transform hover:scale-105"
                              style={{ backgroundColor: color.hex }}
                              data-testid={`color-swatch-${idx}`}
                            />
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-mono text-xs">{color.hex}</p>
                            <p className="text-xs opacity-70">{color.name}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ))}
                  </div>
                )}
                
                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-2 pt-2">
                  <div className="p-2 rounded-md bg-hsl(var(--surface-2)) bg-muted/50">
                    <p className="text-xs text-muted-foreground">Erstellt</p>
                    <p className="text-sm font-medium">
                      {new Date(style.created_at).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div className="p-2 rounded-md bg-muted/50">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className="text-sm font-medium capitalize flex items-center gap-1">
                      {style.status === 'completed' && <CheckCircle2 className="h-3 w-3 text-green-500" />}
                      {style.status}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Psychology Inspector Card */}
          {psychologyData && (psychologyData.biases.length > 0 || psychologyData.triggers.length > 0) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    Psychologie-Inspector
                  </CardTitle>
                  <CardDescription>
                    Analyse der psychologischen Trigger
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cognitive Biases */}
                  {psychologyData.biases.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                        Kognitive Biases
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {psychologyData.biases.map(([bias, count], idx) => (
                          <BiasCard key={idx} bias={bias} count={count} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Dopamine Triggers */}
                  {psychologyData.triggers.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider">
                        Dopamin-Trigger
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {psychologyData.triggers.map(([trigger, count], idx) => (
                          <TriggerCard key={idx} trigger={trigger} count={count} />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Scroll-Stop Power */}
                  {psychologyData.scrollStopPower.total > 0 && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Scroll-Stop-Power
                        </p>
                        <Badge variant="outline" className="text-xs">
                          {psychologyData.scrollStopPower.high}/{psychologyData.scrollStopPower.total} hoch
                        </Badge>
                      </div>
                      <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                        <motion.div 
                          className="h-full bg-gradient-to-r from-primary to-accent"
                          initial={{ width: 0 }}
                          animate={{ 
                            width: `${(psychologyData.scrollStopPower.high / psychologyData.scrollStopPower.total) * 100}%` 
                          }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                        />
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Viral Formula Card */}
          {psychologyData && (psychologyData.alwaysPresent.length > 0 || psychologyData.neverPresent.length > 0) && (
            <motion.div variants={itemVariants}>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-yellow-500" />
                    Virale Formel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Formula Pattern */}
                  {psychologyData.viralFormula && (
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                      <p className="text-sm font-medium">{psychologyData.viralFormula}</p>
                    </div>
                  )}

                  {/* Always Present */}
                  {psychologyData.alwaysPresent.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-1.5 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        IMMER vorhanden
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {psychologyData.alwaysPresent.map((elem, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="border-green-500/50 text-green-700 dark:text-green-300 text-xs"
                          >
                            {elem}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Never Present */}
                  {psychologyData.neverPresent.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-red-500 mb-1.5 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        NIE vorhanden
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {psychologyData.neverPresent.map((elem, idx) => (
                          <Badge 
                            key={idx} 
                            variant="outline" 
                            className="border-red-500/50 text-red-600 dark:text-red-400 text-xs"
                          >
                            {elem}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )}
        </aside>

        {/* Right Content Area */}
        <main className="md:col-span-8 space-y-6 mt-6 md:mt-0">
          {/* Thumbnail Preview Card */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Eye className="h-4 w-4 text-primary" />
                  Thumbnail-Vorschau
                </CardTitle>
              </CardHeader>
              <CardContent>
                {style.sample_thumbnails?.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {style.sample_thumbnails.slice(0, 6).map((thumb, idx) => (
                      <motion.div
                        key={idx}
                        className="aspect-video rounded-lg overflow-hidden border bg-muted group relative"
                        whileHover={{ scale: 1.02 }}
                        transition={{ type: "spring", stiffness: 300 }}
                      >
                        <img
                          src={thumb}
                          alt={`Thumbnail ${idx + 1}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button size="sm" variant="secondary">
                            <ExternalLink className="h-3 w-3 mr-1" />
                            Ansehen
                          </Button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="aspect-[21/9] rounded-lg bg-muted/50 border-2 border-dashed flex items-center justify-center">
                    <p className="text-sm text-muted-foreground">Keine Vorschau-Thumbnails verfügbar</p>
                  </div>
                )}
              </CardContent>
              <CardFooter className="justify-between text-sm text-muted-foreground">
                <span>{style.sample_thumbnails?.length || 0} Beispiel-Thumbnails</span>
                {style.sample_thumbnails?.length > 6 && (
                  <Button variant="ghost" size="sm">
                    Alle anzeigen
                  </Button>
                )}
              </CardFooter>
            </Card>
          </motion.div>

          {/* Tabs Section */}
          <motion.div variants={itemVariants}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="visual" data-testid="tab-visual">
                  <Palette className="h-4 w-4 mr-1" />
                  Visual System
                </TabsTrigger>
                <TabsTrigger value="tips" data-testid="tab-tips">
                  <Lightbulb className="h-4 w-4 mr-1" />
                  Nachbauen-Tipps
                </TabsTrigger>
                <TabsTrigger value="elements" data-testid="tab-elements">
                  <Target className="h-4 w-4 mr-1" />
                  Elemente
                </TabsTrigger>
              </TabsList>

              {/* Visual System Tab */}
              <TabsContent value="visual" className="mt-4 space-y-4">
                {/* Color Palette Full - Editable */}
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">Farbpalette</CardTitle>
                        <CardDescription>{aggregatedStyle.color_mood}</CardDescription>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="text"
                          placeholder="#52B449"
                          className="w-28 h-8 text-xs font-mono"
                          id="new-color-hex"
                          maxLength={7}
                          data-testid="new-color-hex-input"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={async () => {
                            const input = document.getElementById('new-color-hex');
                            let hex = (input?.value || '').trim();
                            if (!hex.startsWith('#')) hex = '#' + hex;
                            if (!/^#[0-9A-Fa-f]{6}$/.test(hex)) {
                              toast.error("Bitte gültigen Hex-Code eingeben (z.B. #52B449)");
                              return;
                            }
                            try {
                              const res = await axios.put(`${API}/styles/${id}/colors`, {
                                action: "add",
                                hex: hex,
                                name: "Custom"
                              });
                              if (res.data.palette) {
                                setStyle(prev => ({
                                  ...prev,
                                  aggregated_style: {
                                    ...prev.aggregated_style,
                                    dominant_palette: res.data.palette
                                  }
                                }));
                                input.value = '';
                                toast.success("Farbe hinzugefügt");
                              }
                            } catch (e) {
                              const msg = e.response?.data?.detail || "Fehler beim Hinzufügen";
                              toast.error(msg);
                            }
                          }}
                          data-testid="add-color-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Farbe
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                      {aggregatedStyle.dominant_palette?.map((color, idx) => (
                        <div key={idx} className="group relative p-3 rounded-lg border hover:border-primary transition-colors text-left">
                          <div className="flex items-center gap-1 absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              className="p-1 rounded bg-background/80 hover:bg-muted"
                              onClick={async () => {
                                try {
                                  await axios.put(`${API}/styles/${id}/colors`, {
                                    action: "remove",
                                    index: idx
                                  });
                                  setStyle(prev => ({
                                    ...prev,
                                    aggregated_style: {
                                      ...prev.aggregated_style,
                                      dominant_palette: prev.aggregated_style.dominant_palette.filter((_, i) => i !== idx)
                                    }
                                  }));
                                  toast.success("Farbe entfernt");
                                } catch (e) { toast.error("Fehler"); }
                              }}
                              data-testid={`remove-color-${idx}`}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </button>
                          </div>
                          <div className="relative">
                            <input
                              type="color"
                              value={color.hex}
                              className="absolute inset-0 w-full h-12 opacity-0 cursor-pointer"
                              onChange={async (e) => {
                                const newHex = e.target.value;
                                try {
                                  await axios.put(`${API}/styles/${id}/colors`, {
                                    action: "update",
                                    index: idx,
                                    hex: newHex
                                  });
                                  setStyle(prev => ({
                                    ...prev,
                                    aggregated_style: {
                                      ...prev.aggregated_style,
                                      dominant_palette: prev.aggregated_style.dominant_palette.map((c, i) => i === idx ? { ...c, hex: newHex } : c)
                                    }
                                  }));
                                } catch (e) { toast.error("Fehler"); }
                              }}
                              data-testid={`color-picker-${idx}`}
                            />
                            <div 
                              className="w-full h-12 rounded-md mb-2 border cursor-pointer"
                              style={{ backgroundColor: color.hex }}
                            />
                          </div>
                          <p className="font-mono text-xs cursor-pointer" onClick={() => handleCopyColor(color.hex)}>{color.hex}</p>
                          <p className="text-xs text-muted-foreground truncate">{color.name}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Style Description */}
                {aggregatedStyle.style_summary_de && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Stil-Beschreibung</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {aggregatedStyle.style_summary_de}
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Typography & Layout */}
                {(aggregatedStyle.typography_style || aggregatedStyle.layout_pattern) && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    {aggregatedStyle.typography_style && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Typografie</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            {aggregatedStyle.typography_style}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    {aggregatedStyle.layout_pattern && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Layout-Muster</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-muted-foreground">
                            {aggregatedStyle.layout_pattern}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Recreation Tips Tab */}
              <TabsContent value="tips" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-accent" />
                      So baust du diesen Stil nach
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {aggregatedStyle.recreation_tips_de?.length > 0 ? (
                      <ul className="space-y-3">
                        {aggregatedStyle.recreation_tips_de.map((tip, idx) => (
                          <motion.li 
                            key={idx}
                            className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: idx * 0.1 }}
                          >
                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-sm font-medium flex items-center justify-center">
                              {idx + 1}
                            </span>
                            <span className="text-sm">{tip}</span>
                          </motion.li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">
                        Keine Nachbauen-Tipps verfügbar
                      </p>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Elements Tab */}
              <TabsContent value="elements" className="mt-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Häufige Elemente</CardTitle>
                    <CardDescription>
                      Diese Elemente kommen in den analysierten Thumbnails vor
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aggregatedStyle.common_elements?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {aggregatedStyle.common_elements.map((elem, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: idx * 0.05 }}
                          >
                            <Badge variant="secondary" className="text-sm">
                              {elem}
                            </Badge>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-6">
                        Keine Elemente gefunden
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Text Elements */}
                {aggregatedStyle.common_text_elements?.length > 0 && (
                  <Card className="mt-4">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Text-Elemente</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {aggregatedStyle.common_text_elements.map((elem, idx) => (
                          <Badge key={idx} variant="outline">
                            {elem}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </motion.div>

          {/* Trend Chart (Mock Data) */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Analyse-Trend
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">Letzte 7 Tage</Badge>
                </div>
              </CardHeader>
              <CardContent>
                <TrendChart data={generateTrendData()} />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Scroll-Stop-Power Score über Zeit
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </main>
      </div>
    </motion.div>
  );
};

export default StyleDetailPage;
